import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Track } from "../data/sharedTypes";
import {
    type FmCountry,
    type FmDiscipline,
    getFmCollegesForCountry,
    getFmDisciplineNameKey,
    getFmSpecialtiesForCountryAndCollege,
} from "../data/foreignMissionsData";
import StepHeader from "../StepHeader";
import FlagIcon from "../components/FlagIcon";

interface Props {
    universityNameKey: string;
    country: FmCountry;
    track: Track;
    onBack: () => void;
    onSelectDiscipline: (discipline: FmDiscipline) => void;
}

/**
 * Foreign Missions — discipline (college) picker (FM-only step).
 *
 * Lists the discipline groupings available for the chosen country+track.
 * Each tile shows specialty count so the user knows how deep the next step
 * will be.
 */
export default function ChooseFmCollege({
    universityNameKey,
    country,
    track,
    onBack,
    onSelectDiscipline,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);

    const disciplines = getFmCollegesForCountry(country.key, track);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_choose_college")}
                subtitle={t(universityNameKey)}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white ring-1 ring-gray-200 overflow-hidden">
                        <FlagIcon iso={country.iso} className="w-full h-full" />
                    </span>
                    {t(`dream_university_country_${country.key}`)}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ${
                    track === "scientific"
                        ? "bg-blue-50 text-blue-700 ring-blue-200"
                        : "bg-purple-50 text-purple-700 ring-purple-200"
                }`}>
                    {t(`dream_university_track_${track}`)}
                </span>
            </div>

            {/* Discipline grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {disciplines.map((discipline) => {
                    const count = getFmSpecialtiesForCountryAndCollege(
                        country.key,
                        discipline.key,
                        track,
                    ).length;
                    return (
                        <button
                            key={discipline.key}
                            onClick={() => onSelectDiscipline(discipline)}
                            className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                        >
                            <span className="text-2xl shrink-0">{discipline.emoji}</span>
                            <div className="flex flex-col gap-0.5 flex-1">
                                <span className="text-sm font-medium text-gray-800">
                                    {t(getFmDisciplineNameKey(discipline.key, country.key))}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {t("dream_university_fm_specialty_count", { count })}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {disciplines.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_fm_no_disciplines_for_track")}
                </p>
            )}
        </div>
    );
}
